import {
  createClient,
  RequestInterceptor,
  ResponseInterceptor,
} from 'react-fetching-library';
import { isAccessTokenExpired, refreshAccessToken } from './RefreshTokenClient';

// In real application this const will be stored in ENV's
const DRIVER_PORTAL_BASEURL = `${process.env.REACT_APP_API_BASE_URL}` || '';

const DRIVER_PORTAL_API_KEY = process.env.REACT_APP_DRIVER_PORTAL_API_KEY || '';

const SERVER_SESSION_FIELDS = ['userId', 'potentialLeadId'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function removeServerSessionFields<T>(value: T): T {
  if (!value) return value;

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    SERVER_SESSION_FIELDS.forEach(field => value.delete(field));
    return value;
  }

  if (!isRecord(value)) return value;

  return Object.keys(value).reduce((result, key) => {
    if (SERVER_SESSION_FIELDS.includes(key)) return result;
    return { ...result, [key]: value[key] };
  }, {} as Record<string, unknown>) as T;
}

const requestHostInterceptor: (
  host: string,
) => RequestInterceptor = host => () => async action => {
  const headers = { ...(action.headers ?? {}) };
  delete headers.Authorization;
  delete headers.authorization;
  const endpoint = /^https?:\/\//.test(action.endpoint)
    ? action.endpoint
    : `${host}${action.endpoint}`;

  return {
    ...action,
    endpoint,
    credentials: 'include',
    body: removeServerSessionFields(action.body),
    headers: { 'x-api-key': DRIVER_PORTAL_API_KEY, ...headers },
  };
};

const refreshTokenInterceptor: ResponseInterceptor = client => async (
  action,
  response,
) => {
  if (
    !isAccessTokenExpired(response.status, response.payload) ||
    action.config?.retryAfterRefresh
  ) {
    return response;
  }

  await refreshAccessToken(DRIVER_PORTAL_BASEURL);

  return client.query(
    {
      ...action,
      config: {
        ...action.config,
        retryAfterRefresh: true,
      },
    },
    true,
  );
};

export const DriverPortalClient = createClient({
  requestInterceptors: [requestHostInterceptor(DRIVER_PORTAL_BASEURL)],
  responseInterceptors: [refreshTokenInterceptor],
});
